import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ShopifyProduct } from '@/services/shopify';
import { useCartStore } from '@/stores/cartStore';
import { useWishlistStore } from '@/stores/wishlistStore';
import { useCurrencyStore } from '@/stores/currencyStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ProductQuickView } from './ProductQuickView';
import { AuthDialog } from '@/components/auth/AuthDialog';
interface ProductCardProps {
  product: ShopifyProduct;
  className?: string;
}
export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  className
}) => {
  const navigate = useNavigate();
  const addItem = useCartStore(state => state.addItem);
  const {
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    fetchWishlist,
    isInitialized
  } = useWishlistStore();
  const {
    formatPrice
  } = useCurrencyStore();
  const [showQuickView, setShowQuickView] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const {
    node
  } = product;
  const price = parseFloat(node.priceRange.minVariantPrice.amount);
  const currencyCode = node.priceRange.minVariantPrice.currencyCode;
  const imageUrl = node.images.edges[0]?.node.url;
  const defaultVariant = node.variants.edges[0]?.node;
  const inWishlist = isInWishlist(node.id);

  // Check auth state and fetch wishlist
  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      setIsLoggedIn(!!user);
      if (user && !isInitialized) {
        fetchWishlist();
      }
    };
    checkAuth();
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session?.user);
      if (session?.user && !isInitialized) {
        fetchWishlist();
      }
    });
    return () => subscription.unsubscribe();
  }, [fetchWishlist, isInitialized]);
  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!defaultVariant) {
      toast.error("Product unavailable", {
        description: "This product is currently unavailable."
      });
      return;
    }
    const cartItem = {
      product,
      variantId: defaultVariant.id,
      variantTitle: defaultVariant.title,
      price: defaultVariant.price,
      quantity: 1,
      selectedOptions: defaultVariant.selectedOptions || []
    };
    addItem(cartItem);
    toast.success("Added to cart", {
      description: `${node.title} has been added to your cart.`
    });
  };
  const handleCardClick = () => {
    navigate(`/product/${node.handle}`);
  };
  const handleQuickView = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowQuickView(true);
  };
  const handleWishlistToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      setShowAuthDialog(true);
      return;
    }
    try {
      if (inWishlist) {
        await removeFromWishlist(node.id);
        toast.success("Removed from wishlist", {
          description: `${node.title} has been removed from your wishlist.`
        });
      } else {
        await addToWishlist(product);
        toast.success("Added to wishlist", {
          description: `${node.title} has been added to your wishlist.`
        });
      }
    } catch (error) {
      toast.error("Error", {
        description: "Failed to update wishlist. Please try again."
      });
    }
  };
  return <>
      <div className={cn('group cursor-pointer', className)} onClick={handleCardClick}>
      {/* Image Container */}
      <div className="relative w-full mb-4 overflow-hidden bg-secondary/10">
        {imageUrl ? <img src={imageUrl} alt={node.title} className="w-full h-auto object-contain transition-transform duration-500 group-hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center bg-secondary/20">
            <span className="text-muted-foreground">No image</span>
          </div>}
        
        {/* Quick Actions */}
        <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="secondary" size="icon" className="h-8 w-8 rounded-full bg-background/90 hover:bg-background" onClick={handleQuickView}>
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="secondary" size="icon" className={cn("h-8 w-8 rounded-full bg-background/90 hover:bg-background", inWishlist && "text-red-500")} onClick={handleWishlistToggle}>
            <Heart className={cn("h-4 w-4", inWishlist && "fill-current")} />
          </Button>
        </div>
      </div>
      
      {/* Product Info */}
      <div className="space-y-2">
        {node.collections?.edges?.[0]?.node.title && <p className="text-xs text-muted-foreground uppercase tracking-wide">
            {node.collections.edges[0].node.title}
          </p>}
        <h3 className="font-medium text-sm leading-tight line-clamp-2 min-h-[2.5rem]">
          {node.title}
        </h3>
        
        <div className="flex items-center justify-between">
          <p className="font-normal text-lg">
            {formatPrice(price, currencyCode)}
          </p>
          
          <Button variant="outline" size="sm" className="h-8 px-3" onClick={handleAddToCart}>
            <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
            Add
          </Button>
        </div>
      </div>
    </div>

    <ProductQuickView product={product} isOpen={showQuickView} onClose={() => setShowQuickView(false)} />

    <AuthDialog isOpen={showAuthDialog} onClose={() => setShowAuthDialog(false)} onSuccess={() => {
      // Retry adding to wishlist after successful auth
      if (!inWishlist) {
        addToWishlist(product);
        toast.success("Added to wishlist", {
          description: `${node.title} has been added to your wishlist.`
        });
      }
    }} />
    </>;
};